import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  DatePicker,
  Form,
  Image,
  Input,
  InputNumber,
  Modal,
  Space,
  Switch,
  Table,
  Tabs,
  Tag,
  Typography,
  Upload,
  message,
} from 'antd';
import { DeleteOutlined, EditOutlined, PlusOutlined, ReloadOutlined, UploadOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import request from '../../api/request';
import './index.css';

const { Text } = Typography;
const { TextArea } = Input;
const HOME_NOTICE_CATEGORY = 'home_notice';
const DEFAULT_HOME_NOTICE = '青楚套餐以及月租，请勿重复申请，开卡失败可更换！';

interface Banner {
  id: number;
  title: string;
  imageUrl: string;
  linkUrl?: string | null;
  sort: number;
  isActive: boolean;
  description?: string | null;
  startDate?: string | null;
  endDate?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

interface Article {
  id: number;
  title: string;
  content: string;
  author: string;
  category: string;
  status: string;
  coverImage?: string | null;
  views?: number;
  createdAt?: string;
  updatedAt?: string;
}

interface BannerFormValues {
  title: string;
  imageUrl: string;
  linkUrl?: string;
  sort?: number;
  isActive?: boolean;
  description?: string;
  startDate?: Dayjs | null;
  endDate?: Dayjs | null;
}

interface NoticeFormValues {
  title: string;
  content: string;
  status: string;
}

const formatDate = (value?: string | null) => {
  if (!value) return '-';
  const date = dayjs(value);
  return date.isValid() ? date.format('YYYY-MM-DD') : String(value);
};

const normalizeNullableText = (value?: string | null) => {
  const trimmed = value?.trim();
  return trimmed || null;
};

const resolveBannerStatus = (banner: Banner) => {
  if (!banner.isActive) return { label: '停用', color: 'default' };
  const today = dayjs().startOf('day');
  if (banner.startDate && dayjs(banner.startDate).startOf('day').isAfter(today)) {
    return { label: '未开始', color: 'blue' };
  }
  if (banner.endDate && dayjs(banner.endDate).endOf('day').isBefore(today)) {
    return { label: '已过期', color: 'red' };
  }
  return { label: '展示中', color: 'green' };
};

const MarketingPage: React.FC = () => {
  const [banners, setBanners] = useState<Banner[]>([]);
  const [notice, setNotice] = useState<Article | null>(null);
  const [loading, setLoading] = useState(false);
  const [noticeLoading, setNoticeLoading] = useState(false);
  const [bannerModalVisible, setBannerModalVisible] = useState(false);
  const [currentBanner, setCurrentBanner] = useState<Banner | null>(null);
  const [savingBanner, setSavingBanner] = useState(false);
  const [uploadingBannerImage, setUploadingBannerImage] = useState(false);
  const [savingNotice, setSavingNotice] = useState(false);
  const [bannerForm] = Form.useForm<BannerFormValues>();
  const [noticeForm] = Form.useForm<NoticeFormValues>();
  const bannerImageUrl = Form.useWatch('imageUrl', bannerForm);

  const fetchBanners = useCallback(async () => {
    setLoading(true);
    try {
      const data = await request.get<any, Banner[]>('/api/marketing/banners');
      setBanners(Array.isArray(data) ? data : []);
    } catch (error: any) {
      message.error(error?.response?.data?.message || '获取首页 Banner 失败');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchNotice = useCallback(async () => {
    setNoticeLoading(true);
    try {
      const data = await request.get<any, Article[]>('/api/content/articles', {
        params: { category: HOME_NOTICE_CATEGORY },
      });
      const currentNotice = Array.isArray(data) && data.length > 0 ? data[0] : null;
      setNotice(currentNotice);
      noticeForm.setFieldsValue({
        title: currentNotice?.title || '首页公告',
        content: currentNotice?.content || DEFAULT_HOME_NOTICE,
        status: currentNotice?.status || 'published',
      });
    } catch (error: any) {
      message.error(error?.response?.data?.message || '获取首页公告失败');
    } finally {
      setNoticeLoading(false);
    }
  }, [noticeForm]);

  const fetchHomeConfig = useCallback(async () => {
    await Promise.all([fetchBanners(), fetchNotice()]);
  }, [fetchBanners, fetchNotice]);

  useEffect(() => {
    fetchHomeConfig();
  }, [fetchHomeConfig]);

  const activeBannerCount = useMemo(
    () => banners.filter((banner) => resolveBannerStatus(banner).label === '展示中').length,
    [banners],
  );

  const openBannerModal = (banner?: Banner) => {
    setCurrentBanner(banner || null);
    bannerForm.setFieldsValue({
      title: banner?.title || '',
      imageUrl: banner?.imageUrl || '',
      linkUrl: banner?.linkUrl || '',
      sort: banner?.sort ?? banners.length + 1,
      isActive: banner?.isActive ?? true,
      description: banner?.description || '',
      startDate: banner?.startDate ? dayjs(banner.startDate) : null,
      endDate: banner?.endDate ? dayjs(banner.endDate) : null,
    });
    setBannerModalVisible(true);
  };

  const toBannerPayload = (values: BannerFormValues) => ({
    title: values.title.trim(),
    imageUrl: values.imageUrl.trim(),
    linkUrl: normalizeNullableText(values.linkUrl),
    sort: Number(values.sort ?? 0),
    isActive: values.isActive ?? true,
    description: normalizeNullableText(values.description),
    startDate: values.startDate ? values.startDate.format('YYYY-MM-DD') : null,
    endDate: values.endDate ? values.endDate.format('YYYY-MM-DD') : null,
  });

  const saveBanner = async () => {
    try {
      const values = await bannerForm.validateFields();
      setSavingBanner(true);
      if (currentBanner) {
        await request.patch(`/api/marketing/banners/${currentBanner.id}`, toBannerPayload(values));
        message.success('Banner 已更新');
      } else {
        await request.post('/api/marketing/banners', toBannerPayload(values));
        message.success('Banner 已创建');
      }
      setBannerModalVisible(false);
      setCurrentBanner(null);
      await fetchBanners();
    } catch (error: any) {
      if (error?.errorFields) return;
      message.error(error?.response?.data?.message || '保存 Banner 失败');
    } finally {
      setSavingBanner(false);
    }
  };

  const uploadBannerImage = async (file: File) => {
    const extOk = /\.(jpe?g|png|webp)$/i.test(file.name);
    const mimeOk = ['image/jpeg', 'image/png', 'image/webp'].includes(file.type);
    if (!extOk || !mimeOk || !file.size || file.size > 5 * 1024 * 1024) {
      message.error('请上传 5MB 内的 jpg/jpeg/png/webp 图片');
      return Upload.LIST_IGNORE;
    }

    const formData = new FormData();
    formData.append('file', file);
    try {
      setUploadingBannerImage(true);
      const data = await request.post<any, { url: string }>('/api/marketing/banners/upload-image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      bannerForm.setFieldValue('imageUrl', data.url);
      message.success('Banner 图片已上传');
    } catch (error: any) {
      message.error(error?.response?.data?.message || '上传 Banner 图片失败');
    } finally {
      setUploadingBannerImage(false);
    }
    return Upload.LIST_IGNORE;
  };

  const toggleBanner = async (banner: Banner, isActive: boolean) => {
    try {
      await request.patch(`/api/marketing/banners/${banner.id}`, { isActive });
      message.success(isActive ? 'Banner 已启用' : 'Banner 已停用');
      await fetchBanners();
    } catch (error: any) {
      message.error(error?.response?.data?.message || '更新 Banner 状态失败');
    }
  };

  const deleteBanner = (banner: Banner) => {
    Modal.confirm({
      title: '删除 Banner',
      content: `确定删除“${banner.title}”吗？客户端首页将不再展示它。`,
      okText: '删除',
      okButtonProps: { danger: true },
      cancelText: '取消',
      onOk: async () => {
        await request.delete(`/api/marketing/banners/${banner.id}`);
        message.success('Banner 已删除');
        await fetchBanners();
      },
    });
  };

  const saveNotice = async () => {
    try {
      const values = await noticeForm.validateFields();
      setSavingNotice(true);
      const payload = {
        title: values.title.trim(),
        content: values.content.trim(),
        author: notice?.author || '管理员',
        category: HOME_NOTICE_CATEGORY,
        status: values.status,
      };
      if (notice?.id) {
        const updated = await request.patch<any, Article>(`/api/content/articles/${notice.id}`, payload);
        setNotice(updated);
        message.success('首页公告已更新');
      } else {
        const created = await request.post<any, Article>('/api/content/articles', payload);
        setNotice(created);
        message.success('首页公告已创建');
      }
      await fetchNotice();
    } catch (error: any) {
      if (error?.errorFields) return;
      message.error(error?.response?.data?.message || '保存首页公告失败');
    } finally {
      setSavingNotice(false);
    }
  };

  const bannerColumns: ColumnsType<Banner> = [
    {
      title: '排序',
      dataIndex: 'sort',
      width: 72,
    },
    {
      title: '图片',
      dataIndex: 'imageUrl',
      width: 140,
      render: (imageUrl) => (
        <Image
          src={imageUrl}
          alt="Banner"
          width={104}
          height={48}
          className="banner-preview"
          fallback="/banner1.png"
        />
      ),
    },
    {
      title: '标题',
      dataIndex: 'title',
      render: (title, record) => (
        <Space direction="vertical" size={2}>
          <Text strong>{title}</Text>
          {record.description && <Text type="secondary">{record.description}</Text>}
        </Space>
      ),
    },
    {
      title: '跳转',
      dataIndex: 'linkUrl',
      width: 220,
      ellipsis: true,
      render: (value) => value || <Text type="secondary">未配置</Text>,
    },
    {
      title: '有效期',
      width: 190,
      render: (_, record) => `${formatDate(record.startDate)} 至 ${formatDate(record.endDate)}`,
    },
    {
      title: '状态',
      width: 150,
      render: (_, record) => {
        const status = resolveBannerStatus(record);
        return (
          <Space>
            <Tag color={status.color}>{status.label}</Tag>
            <Switch
              size="small"
              checked={record.isActive}
              onChange={(checked) => toggleBanner(record, checked)}
            />
          </Space>
        );
      },
    },
    {
      title: '操作',
      width: 150,
      render: (_, record) => (
        <Space>
          <Button size="small" icon={<EditOutlined />} onClick={() => openBannerModal(record)}>
            编辑
          </Button>
          <Button size="small" danger icon={<DeleteOutlined />} onClick={() => deleteBanner(record)}>
            删除
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div className="marketing-container">
      <Alert
        type="info"
        showIcon
        message="首页运营位"
        description="这里配置客户端首页轮播和公告条。没有有效 Banner 时，客户端会自动使用默认轮播图。"
        className="marketing-page-alert"
      />

      <Tabs
        defaultActiveKey="banners"
        items={[
          {
            key: 'banners',
            label: '首页 Banner',
            children: (
              <Card
                title={`Banner 列表（展示中 ${activeBannerCount} 个）`}
                extra={
                  <Space>
                    <Button icon={<ReloadOutlined />} onClick={fetchBanners}>
                      刷新
                    </Button>
                    <Button type="primary" icon={<PlusOutlined />} onClick={() => openBannerModal()}>
                      新增 Banner
                    </Button>
                  </Space>
                }
              >
                <Table
                  rowKey="id"
                  columns={bannerColumns}
                  dataSource={banners}
                  loading={loading}
                  pagination={{ pageSize: 10, showSizeChanger: false }}
                />
              </Card>
            ),
          },
          {
            key: 'notice',
            label: '首页公告',
            children: (
              <Card
                title="公告条配置"
                extra={
                  <Button icon={<ReloadOutlined />} onClick={fetchNotice}>
                    刷新
                  </Button>
                }
                loading={noticeLoading}
              >
                <Form form={noticeForm} layout="vertical" className="notice-form">
                  <Form.Item name="title" label="标题" rules={[{ required: true, message: '请输入标题' }]}>
                    <Input placeholder="首页公告" />
                  </Form.Item>
                  <Form.Item name="content" label="公告文案" rules={[{ required: true, message: '请输入公告文案' }]}>
                    <TextArea rows={4} placeholder={DEFAULT_HOME_NOTICE} showCount maxLength={300} />
                  </Form.Item>
                  <Form.Item name="status" label="发布状态" rules={[{ required: true, message: '请选择状态' }]}>
                    <Switch
                      checkedChildren="发布"
                      unCheckedChildren="草稿"
                      checked={noticeForm.getFieldValue('status') === 'published'}
                      onChange={(checked) => noticeForm.setFieldValue('status', checked ? 'published' : 'draft')}
                    />
                  </Form.Item>
                  <Space>
                    <Button type="primary" loading={savingNotice} onClick={saveNotice}>
                      保存公告
                    </Button>
                    <Text type="secondary">
                      当前分类：{HOME_NOTICE_CATEGORY}
                    </Text>
                  </Space>
                </Form>
              </Card>
            ),
          },
        ]}
      />

      <Modal
        title={currentBanner ? '编辑 Banner' : '新增 Banner'}
        open={bannerModalVisible}
        onCancel={() => {
          setBannerModalVisible(false);
          setCurrentBanner(null);
        }}
        onOk={saveBanner}
        confirmLoading={savingBanner}
        okText="保存"
        cancelText="取消"
        maskClosable={false}
        width={720}
        destroyOnClose
      >
        <Form form={bannerForm} layout="vertical" preserve={false}>
          <Form.Item name="title" label="标题" rules={[{ required: true, message: '请输入标题' }]}>
            <Input placeholder="例如：暑期热门套餐" />
          </Form.Item>
          <Form.Item label="图片 URL" required>
            <Space.Compact className="banner-image-url-input">
              <Form.Item name="imageUrl" noStyle rules={[{ required: true, message: '请输入图片 URL' }]}>
                <Input placeholder="例如：https://example.com/banner.png 或 /api/marketing/assets/banner.png" />
              </Form.Item>
              <Upload accept="image/jpeg,image/png,image/webp" showUploadList={false} beforeUpload={uploadBannerImage}>
                <Button icon={<UploadOutlined />} loading={uploadingBannerImage}>上传图片</Button>
              </Upload>
            </Space.Compact>
            {bannerImageUrl && <Image src={bannerImageUrl} className="banner-form-preview" alt="Banner 预览" />}
          </Form.Item>
          <Form.Item name="linkUrl" label="跳转链接">
            <Input placeholder="例如：/product/123、/order/list、/content/customer_service、https://..." />
          </Form.Item>
          <Form.Item name="description" label="说明">
            <TextArea rows={3} placeholder="运营备注或展示说明" />
          </Form.Item>
          <Space size={16} align="start" className="banner-form-grid">
            <Form.Item name="sort" label="排序" rules={[{ required: true, message: '请输入排序' }]}>
              <InputNumber min={0} precision={0} style={{ width: 140 }} />
            </Form.Item>
            <Form.Item name="startDate" label="开始日期">
              <DatePicker allowClear />
            </Form.Item>
            <Form.Item name="endDate" label="结束日期">
              <DatePicker allowClear />
            </Form.Item>
            <Form.Item name="isActive" label="启用" valuePropName="checked">
              <Switch checkedChildren="启用" unCheckedChildren="停用" />
            </Form.Item>
          </Space>
        </Form>
      </Modal>
    </div>
  );
};

export default MarketingPage;
